import { type Interval } from 'date-fns';

import { type AkaVeranstaltungId, Location, type Raum, type Zeitraum } from '../../../../../dtos';
import { mergeConsecutiveIntervals } from '../../../../../utils/dateAndIntervalHelpers.js';
import { type CalendarEvent, CalendarRaumEvent } from '../CalendarEvent';
import { type CalendarRaumResourceType } from '../calendarResourceTypes';
import { AbstractCalendarResource } from './AbstractCalendarResource';
import { type IntervalWithOptionalVeranstaltung } from './AbstractCalendarResource.types.js';

export abstract class AbstractRaumResource extends AbstractCalendarResource {
  public abstract readonly type: CalendarRaumResourceType;

  public abstract readonly name: string;

  public readonly raum: Raum;

  public readonly location: Location;

  public constructor(raum: Raum, location: Location) {
    super();
    this.raum = raum;
    this.location = location;
  }

  public getRessourceSapId(): number {
    return this.raum.raumSapId;
  }

  public createFixedEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId | null): CalendarEvent[] {
    const events: CalendarRaumEvent[] = [];

    for (const blockung of this.raum.raumBlockungen) {
      for (const interval of mergeConsecutiveIntervals(blockung.zeitraeume)) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isOldSelection = blockung.akaVeranstaltungId === akaVeranstaltungId;

        events.push(newEvent);
      }
    }

    return events;
  }

  protected createEventFromBlockungAndInterval(
    blockung: Raum['raumBlockungen'][number],
    interval: Interval<Date, Date>,
  ) {
    return new CalendarRaumEvent(interval.start, interval.end, this, {
      raumgroesse: blockung.raumgroesse,
      quelleTerminId: blockung.akaVeranstaltungId,
      veranstaltung: blockung.veranstaltung,
      zeitraeume: blockung.zeitraeume,
    });
  }

  public createNewCalendarEventByInterval(interval: Interval<Date, Date>): CalendarRaumEvent {
    const event = new CalendarRaumEvent(interval.start, interval.end, this, {
      // unimportant values
      locationKuerzel: null,
      quelleTerminId: '',
      veranstaltung: null,

      // important values
      zeitraeume: this.createZeitraeumeByInterval(interval),
    });
    event.isCurrentSelection = true;

    return event;
  }

  protected abstract computeNewCalendarEventBounds(date: Date, ablaufTageCount: number): Zeitraum;

  protected getBlockungen(): IntervalWithOptionalVeranstaltung[] {
    return this.raum.raumBlockungen;
  }
}
